import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Modal, Button, Spinner } from '../ui';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { Upload, FileUp, Download, CheckCircle, AlertCircle, X, Save } from 'lucide-react';
import api from '@/config/axios';

const BulkImportModal = ({ isOpen, onClose, onSuccess }) => {
    const { error: showError, success: showSuccess } = useToastContext();
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('upload'); // upload, preview, processing
    const [uploadError, setUploadError] = useState(null);
    const [importResults, setImportResults] = useState(null);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && selectedFile.type !== 'application/vnd.ms-excel') {
                showError('Please upload a valid Excel file (.xlsx or .xls)');
                return;
            }
            setFile(selectedFile);
            parseFile(selectedFile);
        }
    };

    const parseFile = (file) => {
        setLoading(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '', cellDates: true });

                if (jsonData.length === 0) {
                    setUploadError('The uploaded file is empty.');
                    setFile(null);
                } else {
                    // Map generic Excel headers to internal state keys
                    const processedData = jsonData.map((row, index) => {
                        const type = row['TYPE'] || row['type'] || row['transactionType'];
                        const isInr = type === 'INR';

                        let date = row['DATE'] || row['date'];
                        if (date && date instanceof Date) {
                            const d = date.getDate().toString().padStart(2, '0');
                            const m = (date.getMonth() + 1).toString().padStart(2, '0');
                            const y = date.getFullYear();
                            date = `${d}/${m}/${y}`;
                        }

                        return {
                            _id: index,
                            transactionType: type,
                            date: date,
                            // Map AMOUNT
                            transactionAmountInr: isInr ? (row['AMOUNT (USDT)'] || row['AMOUNT (INR/AED)'] || row['amount']) : undefined,
                            amount: !isInr ? (row['AMOUNT (USDT)'] || row['AMOUNT (INR/AED)'] || row['amount']) : undefined,
                            // Map RATES
                            aedToUsdtRate: row['AED/USDT RATE'] || row['aedToUsdtRate'],
                            inrToAedRate: row['INR/AED RATE'] || row['inrToAedRate'],
                            // Map USDT SELL (context dependent)
                            usdtSellingInr: isInr ? (row['USDT SELLING INR'] || row['USDT SELL RATE'] || row['usdtSellingInr']) : undefined,
                            usdtSellingRate: !isInr ? (row['USDT SELL RATE'] || row['usdtSellingRate']) : undefined,
                            // Map USDT BUY
                            usdtBuyingRate: row['USDT BUY RATE'] || row['usdtBuyingRate']
                        };
                    });
                    setData(processedData);
                    setStep('preview');
                    setUploadError(null);
                }
            } catch (err) {
                console.error('Error parsing file:', err);
                setUploadError('Failed to parse the file. Please ensure it is a valid Excel file.');
                setFile(null);
            } finally {
                setLoading(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'TYPE': 'INR',
                'DATE': '27/10/2023',
                'AMOUNT (USDT)': 1000.00,
                'AED/USDT RATE': 3.67,
                'INR/AED RATE': 22.50,
                'USDT SELLING INR': 83.50
            },
            {
                'TYPE': 'INR',
                'DATE': '28/10/2023',
                'AMOUNT (USDT)': 2500.00,
                'AED/USDT RATE': 3.67,
                'INR/AED RATE': 22.45,
                'USDT SELLING INR': 83.40
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
        XLSX.writeFile(workbook, 'transaction_import_template.xlsx');
    };

    const handleCellChange = (id, field, value) => {
        setData(prevData => prevData.map(row => {
            if (row._id === id) {
                return { ...row, [field]: value };
            }
            return row;
        }));
    };

    const handleRemoveRow = (id) => {
        setData(prevData => prevData.filter(row => row._id !== id));
    };

    const validateData = () => {
        // Basic validation
        const validRows = data.filter(row => {
            if (!row.transactionType) return false;
            if (row.transactionType === 'INR') {
                return row.transactionAmountInr && row.aedToUsdtRate && row.inrToAedRate && row.usdtSellingInr;
            }
            if (row.transactionType === 'UAE') {
                return row.amount && row.usdtBuyingRate && row.usdtSellingRate;
            }
            return false;
        });

        if (validRows.length === 0) {
            showError('No valid rows found to import.');
            return false;
        }

        if (validRows.length !== data.length) {
            // Warn user but allow proceed? Or just filter?
            // For now, let's filter automatically and notify
            showError(`${data.length - validRows.length} invalid rows found. Please fix or remove them.`);
            return false;
        }

        return true;
    };

    const handleImport = async () => {
        if (!validateData()) return;

        setLoading(true);
        setStep('processing');

        try {
            // Prepare data for API
            const transactionsToImport = data.map(({ _id, ...rest }) => ({
                ...rest,
                // Ensure numbers are parsed
                transactionAmountInr: rest.transactionAmountInr ? parseFloat(rest.transactionAmountInr) : undefined,
                aedToUsdtRate: rest.aedToUsdtRate ? parseFloat(rest.aedToUsdtRate) : undefined,
                inrToAedRate: rest.inrToAedRate ? parseFloat(rest.inrToAedRate) : undefined,
                usdtSellingInr: rest.usdtSellingInr ? parseFloat(rest.usdtSellingInr) : undefined,
                amount: rest.amount ? parseFloat(rest.amount) : undefined,
                usdtBuyingRate: rest.usdtBuyingRate ? parseFloat(rest.usdtBuyingRate) : undefined,
                usdtSellingRate: rest.usdtSellingRate ? parseFloat(rest.usdtSellingRate) : undefined,
            }));

            // Split into chunks of 50 to avoid timeout
            const chunkSize = 50;
            const chunks = [];
            for (let i = 0; i < transactionsToImport.length; i += chunkSize) {
                chunks.push(transactionsToImport.slice(i, i + chunkSize));
            }

            let totalProcessed = 0;
            let totalFailed = 0;
            const allErrors = [];

            for (const chunk of chunks) {
                const response = await api.post('/api/portfolio/bulk-transactions', { transactions: chunk });
                if (response.data.success) {
                    totalProcessed += response.data.processed;
                    totalFailed += response.data.failed;
                    if (response.data.errors) {
                        allErrors.push(...response.data.errors);
                    }
                }
            }

            setImportResults({
                processed: totalProcessed,
                failed: totalFailed,
                errors: allErrors
            });

            if (totalProcessed > 0) {
                showSuccess(`Successfully imported ${totalProcessed} transactions.`);
                if (onSuccess) onSuccess();
            }

            if (totalFailed > 0) {
                showError(`Failed to import ${totalFailed} transactions.`);
            }

        } catch (error) {
            console.error('Import error:', error);
            setUploadError(error.response?.data?.message || 'Failed to import transactions');
            setStep('preview'); // Go back to preview on critical error
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setData([]);
        setStep('upload');
        setUploadError(null);
        setImportResults(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const renderUploadStep = () => (
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-primary-200 dark:border-primary-700 rounded-lg bg-primary-50 dark:bg-primary-900/30">
            <FileUp size={48} className="text-primary-400 mb-4" />
            <h3 className="text-lg font-medium text-primary-900 dark:text-primary-50 mb-2">
                Upload Transaction File
            </h3>
            <p className="text-sm text-primary-600 dark:text-primary-400 mb-6 text-center">
                Drag and drop your Excel file here, or click to browse.
                <br />
                Supported formats: .xlsx, .xls
            </p>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx, .xls"
                className="hidden"
            />

            <div className="flex gap-4">
                <Button
                    variant="outline"
                    onClick={downloadTemplate}
                    leftIcon={<Download size={16} />}
                >
                    Download Template
                </Button>
                <Button
                    onClick={() => fileInputRef.current?.click()}
                    leftIcon={<Upload size={16} />}
                >
                    Select File
                </Button>
            </div>

            {uploadError && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md flex items-center gap-2 text-sm">
                    <AlertCircle size={16} />
                    {uploadError}
                </div>
            )}
        </div>
    );

    const renderPreviewStep = () => (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-primary-600 dark:text-primary-400">
                    Previewing {data.length} transactions
                </div>
                <div className="flex gap-2">
                    <Button variant="ghost" onClick={handleReset} size="sm">
                        Cancel
                    </Button>
                    <Button onClick={handleImport} size="sm" isLoading={loading}>
                        Confirm Import
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-auto border border-primary-200 dark:border-primary-700 rounded-lg">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-primary-700 uppercase bg-primary-50 dark:bg-primary-800 dark:text-primary-400 sticky top-0">
                        <tr>
                            <th className="px-3 py-2">ACTION</th>
                            <th className="px-3 py-2">TYPE</th>
                            <th className="px-3 py-2">DATE</th>
                            <th className="px-3 py-2">AMOUNT (USDT)</th>
                            <th className="px-3 py-2">AED/USDT RATE</th>
                            <th className="px-3 py-2">INR/AED RATE</th>
                            <th className="px-3 py-2">USDT SELLING INR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row) => (
                            <tr key={row._id} className="bg-white border-b dark:bg-primary-900 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-800/50">
                                <td className="px-3 py-2">
                                    <button onClick={() => handleRemoveRow(row._id)} className="text-red-500 hover:text-red-700">
                                        <X size={14} />
                                    </button>
                                </td>
                                <td className="px-3 py-2">
                                    <select
                                        value={row.transactionType || 'INR'}
                                        onChange={(e) => handleCellChange(row._id, 'transactionType', e.target.value)}
                                        className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white text-xs w-16"
                                    >
                                        <option value="INR">INR</option>
                                        <option value="UAE">UAE</option>
                                    </select>
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="text"
                                        value={row.date || ''}
                                        onChange={(e) => handleCellChange(row._id, 'date', e.target.value)}
                                        className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white text-xs w-24"
                                        placeholder="DD/MM/YYYY"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="number"
                                        value={row.transactionAmountInr || ''}
                                        onChange={(e) => handleCellChange(row._id, 'transactionAmountInr', e.target.value)}
                                        className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white text-xs w-24"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="number"
                                        value={row.aedToUsdtRate || ''}
                                        onChange={(e) => handleCellChange(row._id, 'aedToUsdtRate', e.target.value)}
                                        className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white text-xs w-20"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="number"
                                        value={row.inrToAedRate || ''}
                                        onChange={(e) => handleCellChange(row._id, 'inrToAedRate', e.target.value)}
                                        className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white text-xs w-20"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="number"
                                        value={row.usdtSellingInr || ''}
                                        onChange={(e) => handleCellChange(row._id, 'usdtSellingInr', e.target.value)}
                                        className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white text-xs w-20"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderProcessingStep = () => (
        <div className="flex flex-col items-center justify-center py-12">
            {importResults ? (
                <>
                    {importResults.failed === 0 ? (
                        <CheckCircle size={48} className="text-green-500 mb-4" />
                    ) : (
                        <AlertCircle size={48} className="text-yellow-500 mb-4" />
                    )}

                    <h3 className="text-xl font-medium text-primary-900 dark:text-primary-50 mb-2">
                        Import Complete
                    </h3>
                    <p className="text-primary-600 dark:text-primary-400 mb-6 text-center">
                        Processed: {importResults.processed} <br />
                        Failed: {importResults.failed}
                    </p>

                    {importResults.errors && importResults.errors.length > 0 && (
                        <div className="w-full max-h-40 overflow-auto bg-red-50 dark:bg-red-900/20 p-3 rounded-lg mb-6 text-xs code">
                            {importResults.errors.map((err, idx) => (
                                <div key={idx} className="text-red-600 dark:text-red-400 border-b border-red-100 dark:border-red-800 last:border-0 py-1">
                                    Row {err.index + 1}: {err.message}
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex gap-4">
                        <Button onClick={handleReset} variant="outline">
                            Import More
                        </Button>
                        <Button onClick={onClose}>
                            Close
                        </Button>
                    </div>
                </>
            ) : (
                <>
                    <Spinner size="xl" className="mb-4" />
                    <h3 className="text-lg font-medium text-primary-900 dark:text-primary-50">
                        Importing transactions...
                    </h3>
                    <p className="text-sm text-primary-600 dark:text-primary-400 mt-2">
                        This may take a moment while we calculate profits and distributions.
                    </p>
                </>
            )}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Bulk Import Transactions"
            size="xl" // Make it wider for the table
            closeOnOverlayClick={false}
        >
            <div className="p-1">
                {step === 'upload' && renderUploadStep()}
                {step === 'preview' && renderPreviewStep()}
                {step === 'processing' && renderProcessingStep()}
            </div>
        </Modal>
    );
};

export default BulkImportModal;
